import { useEffect, useState, useCallback, type MouseEvent } from 'react'
import * as AccordionPrimitive from '@radix-ui/react-accordion'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
} from '@/components/ui/accordion'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Package,
  Building2,
  ChevronDownIcon,
  Tag,
  Palette,
  Settings2,
  Shield,
  Users,
} from 'lucide-react'
import { NodeIcon } from '@/components/ui/NodeIcon'
import type { NodeColor } from '@/lib/node-colors'
import { NODE_COLOR_OPTIONS } from '@/lib/node-colors'
import { cn } from '@/lib/utils'
import { DataClassificationSection } from './sections/DataClassificationSection'
import { NodeDataAttributesSection } from './sections/NodeDataAttributesSection'
import { PrivacyGovernancePanel } from './sections/PrivacyGovernancePanel'
import { OwnershipSection, type OwnerInfo, type DepartmentInfo } from './sections/OwnershipSection'
import { DynamicConfigSection, type ConfigSchema } from './sections/DynamicConfigSection'

interface NodeData {
  // Basic Info (Editable)
  label?: string
  description?: string
  color?: NodeColor
  icon?: string
  vendor?: string

  // Identity (Read-Only - Set at Creation)
  product?: {
    id: string
    name: string
    code: string
    type?: string
  }
  brand?: {
    id: string
    name: string
    code: string
  }

  // Ownership (New - Editable) - PROJ-045
  ownerId?: string
  owner?: OwnerInfo
  departmentIds?: string[]
  departments?: DepartmentInfo[]

  // Product Configuration (Dynamic - Per Product) - PROJ-045
  productConfig?: Record<string, unknown>
  configSchema?: {
    type: 'object'
    properties: Record<string, {
      type: 'string' | 'number' | 'boolean' | 'array' | 'object'
      title: string
      description?: string
      enum?: string[]
      default?: unknown
      format?: 'password' | 'uri' | 'email' | 'date' | 'textarea'
      required?: boolean
    }>
    required?: string[]
    groups?: Array<{
      title: string
      icon?: string
      fields: string[]
    }>
  }

  // Data Governance (Fixed)
  dataClassificationCode?: string | null
  dataAttributeCodes?: string[]
}

interface NodePropertiesPanelProps {
  nodeId: string
  data: NodeData
  onChange: (updates: Partial<NodeData>) => void
  nodeType: 'trigger' | 'action'
  /** Called when drag starts (for FloatingPanel integration) */
  onDragStart?: (e: MouseEvent) => void
}

/** Color swatch button for the color picker */
function ColorSwatch({
  color,
  selected,
  onClick,
}: {
  color: NodeColor
  selected: boolean
  onClick: () => void
}) {
  const colorClasses: Record<NodeColor, string> = {
    neutral: 'bg-neutral-500',
    sky: 'bg-sky-500',
    emerald: 'bg-emerald-500',
    amber: 'bg-amber-500',
    violet: 'bg-violet-500',
  }

  const colorNames: Record<NodeColor, string> = {
    neutral: 'Gris',
    sky: 'Azul',
    emerald: 'Verde',
    amber: 'Ámbar',
    violet: 'Violeta',
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onClick}
            className={cn(
              'w-6 h-6 rounded-full transition-all',
              colorClasses[color],
              selected
                ? 'ring-2 ring-offset-2 ring-offset-background ring-primary scale-110'
                : 'hover:scale-110'
            )}
            aria-label={`Color ${colorNames[color]}`}
          />
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          {colorNames[color]}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export default function NodePropertiesPanel({ nodeId, data, onChange, nodeType, onDragStart }: NodePropertiesPanelProps) {
  const [localLabel, setLocalLabel] = useState(data.label || '')
  const [localDescription, setLocalDescription] = useState(data.description || '')

  // Update local state when data changes from outside
  useEffect(() => {
    setLocalLabel(data.label || '')
    setLocalDescription(data.description || '')
  }, [data.label, data.description])

  // Handle label change with debounce
  const handleLabelChange = (value: string) => {
    setLocalLabel(value)
    onChange({ label: value })
  }

  // Handle description change
  const handleDescriptionChange = (value: string) => {
    setLocalDescription(value)
    onChange({ description: value })
  }

  // Handle color change
  const handleColorChange = (color: NodeColor) => {
    onChange({ color })
  }

  // Handle owner change - PROJ-045
  const handleOwnerChange = useCallback((owner: OwnerInfo | null) => {
    if (owner) {
      onChange({ ownerId: owner.id, owner })
    } else {
      onChange({ ownerId: undefined, owner: undefined })
    }
  }, [onChange])

  // Handle departments change - PROJ-045
  const handleDepartmentsChange = useCallback((departments: DepartmentInfo[]) => {
    onChange({
      departmentIds: departments.map((d) => d.id),
      departments
    })
  }, [onChange])

  // Handle product config change - PROJ-045
  const handleProductConfigChange = useCallback((productConfig: Record<string, unknown>) => {
    onChange({ productConfig })
  }, [onChange])

  // Determine which accordion sections to show by default
  const defaultOpenSections = ['identification', 'appearance']
  if (data.product || data.brand) defaultOpenSections.push('product')
  if (data.configSchema) defaultOpenSections.push('configuration')

  return (
    <div className="pointer-events-auto">
      {/* Header - draggable when onDragStart is provided */}
      <div
        className={cn(
          "p-3 border-b flex items-center justify-between",
          onDragStart && "cursor-grab active:cursor-grabbing select-none"
        )}
        onMouseDown={onDragStart}
      >
        <div>
          <h3 className="text-sm font-semibold">Propiedades del Nodo</h3>
          <p className="text-[11px] text-muted-foreground">
            {nodeType === 'trigger' ? 'Nodo Trigger' : 'Nodo Action'} - Configuración
          </p>
        </div>
        {data.icon && (
          <NodeIcon icon={data.icon} size="lg" />
        )}
      </div>

      <Accordion
        type="multiple"
        className="w-full"
        defaultValue={defaultOpenSections}
      >
        {/* Section 1: Identificación */}
        <AccordionItem value="identification" className="py-1">
          <AccordionPrimitive.Header className="flex">
            <AccordionPrimitive.Trigger className="flex flex-1 items-center justify-between px-3 py-2 text-left text-[13px] leading-6 font-semibold transition-all outline-none hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring/50 [&[data-state=open]>svg]:rotate-180">
              <span className="flex items-center gap-2">
                <span
                  className="flex size-8 shrink-0 items-center justify-center rounded-full border bg-background"
                  aria-hidden="true"
                >
                  <Tag size={14} className="opacity-60" />
                </span>
                <span className="flex flex-col">
                  <span>Identificación</span>
                  <span className="text-[11px] font-normal text-muted-foreground">
                    Nombre y descripción del nodo
                  </span>
                </span>
              </span>
              <ChevronDownIcon
                size={14}
                className="pointer-events-none shrink-0 opacity-60 transition-transform duration-200"
                aria-hidden="true"
              />
            </AccordionPrimitive.Trigger>
          </AccordionPrimitive.Header>
          <AccordionContent className="px-3 pb-3">
            <div className="ml-10 space-y-3">
              {/* Label */}
              <div className="space-y-1.5">
                <Label htmlFor="node-label" className="text-xs">
                  Nombre del Nodo
                </Label>
                <Input
                  id="node-label"
                  type="text"
                  value={localLabel}
                  onChange={(e) => handleLabelChange(e.target.value)}
                  placeholder="Nombre del nodo..."
                  className="h-8 text-sm"
                />
              </div>

              {/* Description */}
              <div className="space-y-1.5">
                <Label htmlFor="node-description" className="text-xs">
                  Descripción / Notas
                </Label>
                <Textarea
                  id="node-description"
                  value={localDescription}
                  onChange={(e) => handleDescriptionChange(e.target.value)}
                  placeholder="Añade notas sobre este nodo..."
                  className="min-h-[60px] text-xs resize-none"
                />
              </div>

              {/* Node Type (Read-only) */}
              <div className="space-y-1.5">
                <Label className="text-xs">Tipo de Nodo</Label>
                <div className="text-xs text-muted-foreground bg-muted px-2 py-1.5 rounded-md">
                  <span className="capitalize">{nodeType === 'trigger' ? 'Trigger' : 'Action'}</span>
                  {data.vendor && !data.product && (
                    <span className="ml-2">• {data.vendor}</span>
                  )}
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Section 2: Apariencia */}
        <AccordionItem value="appearance" className="py-1">
          <AccordionPrimitive.Header className="flex">
            <AccordionPrimitive.Trigger className="flex flex-1 items-center justify-between px-3 py-2 text-left text-[13px] leading-6 font-semibold transition-all outline-none hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring/50 [&[data-state=open]>svg]:rotate-180">
              <span className="flex items-center gap-2">
                <span
                  className="flex size-8 shrink-0 items-center justify-center rounded-full border bg-background"
                  aria-hidden="true"
                >
                  <Palette size={14} className="opacity-60" />
                </span>
                <span className="flex flex-col">
                  <span>Apariencia</span>
                  <span className="text-[11px] font-normal text-muted-foreground">
                    Color y estilo visual
                  </span>
                </span>
              </span>
              <ChevronDownIcon
                size={14}
                className="pointer-events-none shrink-0 opacity-60 transition-transform duration-200"
                aria-hidden="true"
              />
            </AccordionPrimitive.Trigger>
          </AccordionPrimitive.Header>
          <AccordionContent className="px-3 pb-3">
            <div className="ml-10 space-y-3">
              {/* Color Picker */}
              <div className="space-y-1.5">
                <Label className="text-xs">Color del Nodo</Label>
                <div className="flex items-center gap-2">
                  {NODE_COLOR_OPTIONS.map((color) => (
                    <ColorSwatch
                      key={color}
                      color={color}
                      selected={data.color === color || (!data.color && color === 'neutral')}
                      onClick={() => handleColorChange(color)}
                    />
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground">
                  Actual: <span className="font-medium capitalize">{data.color || 'neutral'}</span>
                </p>
              </div>

              {/* Icon Display - Solo si no hay producto (nodos genéricos) */}
              {data.icon && !data.product && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Icono</Label>
                  <div className="flex items-center justify-center h-10 w-10 border rounded-lg bg-muted/30">
                    <NodeIcon icon={data.icon} size="lg" />
                  </div>
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Section 3: Producto (solo si hay product o brand) */}
        {(data.product || data.brand) && (
          <AccordionItem value="product" className="py-1">
            <AccordionPrimitive.Header className="flex">
              <AccordionPrimitive.Trigger className="flex flex-1 items-center justify-between px-3 py-2 text-left text-[13px] leading-6 font-semibold transition-all outline-none hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring/50 [&[data-state=open]>svg]:rotate-180">
                <span className="flex items-center gap-2">
                  <span
                    className="flex size-8 shrink-0 items-center justify-center rounded-full border bg-background"
                    aria-hidden="true"
                  >
                    <Package size={14} className="opacity-60" />
                  </span>
                  <span className="flex flex-col">
                    <span>Producto</span>
                    <span className="text-[11px] font-normal text-muted-foreground">
                      Información del producto asociado
                    </span>
                  </span>
                </span>
                <ChevronDownIcon
                  size={14}
                  className="pointer-events-none shrink-0 opacity-60 transition-transform duration-200"
                  aria-hidden="true"
                />
              </AccordionPrimitive.Trigger>
            </AccordionPrimitive.Header>
            <AccordionContent className="px-3 pb-3">
              <div className="ml-10 space-y-3">
                {data.product && (
                  <div className="space-y-1.5">
                    <Label className="text-xs flex items-center gap-1.5 text-muted-foreground">
                      <Package className="h-3 w-3" />
                      Producto
                    </Label>
                    <div className="flex items-center gap-2 p-2 border rounded-md bg-muted/30">
                      {data.icon && <NodeIcon icon={data.icon} size="md" />}
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">{data.product.name}</span>
                        <span className="text-[10px] text-muted-foreground">{data.product.code}</span>
                      </div>
                    </div>
                  </div>
                )}
                {data.brand && (
                  <div className="space-y-1.5">
                    <Label className="text-xs flex items-center gap-1.5 text-muted-foreground">
                      <Building2 className="h-3 w-3" />
                      Marca
                    </Label>
                    <div className="flex flex-col p-2 border rounded-md bg-muted/30">
                      <span className="text-sm font-medium">{data.brand.name}</span>
                      <span className="text-[10px] text-muted-foreground">{data.brand.code}</span>
                    </div>
                  </div>
                )}
                <p className="text-[10px] text-muted-foreground italic">
                  La identidad del nodo se establece en la creación
                </p>
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Section 4: Propiedad (Ownership) */}
        <AccordionItem value="ownership" className="py-1">
          <AccordionPrimitive.Header className="flex">
            <AccordionPrimitive.Trigger className="flex flex-1 items-center justify-between px-3 py-2 text-left text-[13px] leading-6 font-semibold transition-all outline-none hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring/50 [&[data-state=open]>svg]:rotate-180">
              <span className="flex items-center gap-2">
                <span
                  className="flex size-8 shrink-0 items-center justify-center rounded-full border bg-background"
                  aria-hidden="true"
                >
                  <Users size={14} className="opacity-60" />
                </span>
                <span className="flex flex-col">
                  <span>Propiedad</span>
                  <span className="text-[11px] font-normal text-muted-foreground">
                    Responsable y departamentos
                  </span>
                </span>
              </span>
              <ChevronDownIcon
                size={14}
                className="pointer-events-none shrink-0 opacity-60 transition-transform duration-200"
                aria-hidden="true"
              />
            </AccordionPrimitive.Trigger>
          </AccordionPrimitive.Header>
          <AccordionContent className="px-3 pb-3">
            <div className="ml-10">
              <OwnershipSection
                nodeId={nodeId}
                ownerId={data.ownerId}
                owner={data.owner}
                onOwnerChange={handleOwnerChange}
                departmentIds={data.departmentIds}
                departments={data.departments}
                onDepartmentsChange={handleDepartmentsChange}
              />
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Section 5: Configuración (solo si hay configSchema) */}
        {data.configSchema && (
          <AccordionItem value="configuration" className="py-1">
            <AccordionPrimitive.Header className="flex">
              <AccordionPrimitive.Trigger className="flex flex-1 items-center justify-between px-3 py-2 text-left text-[13px] leading-6 font-semibold transition-all outline-none hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring/50 [&[data-state=open]>svg]:rotate-180">
                <span className="flex items-center gap-2">
                  <span
                    className="flex size-8 shrink-0 items-center justify-center rounded-full border bg-background"
                    aria-hidden="true"
                  >
                    <Settings2 size={14} className="opacity-60" />
                  </span>
                  <span className="flex flex-col">
                    <span>Configuración</span>
                    <span className="text-[11px] font-normal text-muted-foreground">
                      Parámetros del producto
                    </span>
                  </span>
                </span>
                <ChevronDownIcon
                  size={14}
                  className="pointer-events-none shrink-0 opacity-60 transition-transform duration-200"
                  aria-hidden="true"
                />
              </AccordionPrimitive.Trigger>
            </AccordionPrimitive.Header>
            <AccordionContent className="px-3 pb-3">
              <div className="ml-10">
                <DynamicConfigSection
                  nodeId={nodeId}
                  schema={data.configSchema as ConfigSchema}
                  config={data.productConfig}
                  onChange={handleProductConfigChange}
                  productName={data.product?.name}
                />
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Section 6: Gobernanza de Datos */}
        <AccordionItem value="governance" className="py-1">
          <AccordionPrimitive.Header className="flex">
            <AccordionPrimitive.Trigger className="flex flex-1 items-center justify-between px-3 py-2 text-left text-[13px] leading-6 font-semibold transition-all outline-none hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring/50 [&[data-state=open]>svg]:rotate-180">
              <span className="flex items-center gap-2">
                <span
                  className="flex size-8 shrink-0 items-center justify-center rounded-full border bg-background"
                  aria-hidden="true"
                >
                  <Shield size={14} className="opacity-60" />
                </span>
                <span className="flex flex-col">
                  <span>Gobernanza de Datos</span>
                  <span className="text-[11px] font-normal text-muted-foreground">
                    Clasificación y atributos
                  </span>
                </span>
              </span>
              <ChevronDownIcon
                size={14}
                className="pointer-events-none shrink-0 opacity-60 transition-transform duration-200"
                aria-hidden="true"
              />
            </AccordionPrimitive.Trigger>
          </AccordionPrimitive.Header>
          <AccordionContent className="px-3 pb-3">
            <div className="ml-10 space-y-4">
              {/* Data Classification Section */}
              <DataClassificationSection
                nodeId={nodeId}
                classificationCode={data.dataClassificationCode || null}
                onClassificationChange={(code) => {
                  onChange({ dataClassificationCode: code })
                }}
              />

              {/* Data Attributes Section */}
              <NodeDataAttributesSection
                nodeId={nodeId}
                onAttributesChange={(codes) => {
                  onChange({ dataAttributeCodes: codes })
                }}
              />

              {/* Privacy Governance Section - PROJ-082 */}
              <PrivacyGovernancePanel nodeId={nodeId} />
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  )
}
